import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import axios from 'axios'
import '../styles/searchWizard.css'
import { getFlagFallback, getFlagUrl } from '../utils/countryFlags'
import { includesCountry } from '../utils/countryNames'
import { buildIndicatorSelectionKey, normalizeTopicName } from '../utils/searchKeys'

function PanelHeader({ step, title, summary, isOpen, isDone, onToggle }) {
  return (
    <div className="panel-header" onClick={onToggle}>
      <div className="panel-header-left">
        <span className={`panel-step-badge${isDone ? ' done' : ''}`}>{isDone ? '✓' : step}</span>
        <span className="panel-title">{title}</span>
        {summary && <span className="panel-summary">{summary}</span>}
      </div>
      <span className={`panel-chevron${isOpen ? ' open' : ''}`}>▼</span>
    </div>
  )
}

function SearchWizard({
  countryList,
  indicatorList,
  topicList,
  areaList,
  loading,
  onSearch,
  onResetAll,
  initialIndicator,
  initialIndicatorId,
  initialTopicId,
}) {
  const [openPanel, setOpenPanel] = useState('countries')

  const getTopicSortWeight = useCallback((areaLabel, topicLabel) => {
    const normalizedArea = String(areaLabel || '').trim().toLowerCase()
    const normalizedTopic = String(topicLabel || '').trim().toLowerCase()

    if (!normalizedArea.includes('fiscal accounts') && !normalizedArea.includes('public debt')) {
      return Number.MAX_SAFE_INTEGER
    }

    if (normalizedTopic === 'central government') return 0
    if (normalizedTopic === 'general government') return 1
    if (normalizedTopic === 'non financial public sector') return 2

    return Number.MAX_SAFE_INTEGER
  }, [])

  const [selectedCountries, setSelectedCountries] = useState([])
  const [countrySearch, setCountrySearch] = useState('')
  const [selectedIndicators, setSelectedIndicators] = useState([])
  const [indicatorSearch, setIndicatorSearch] = useState('')
  const [indicatorAvailability, setIndicatorAvailability] = useState({})
  const [loadingAvailability, setLoadingAvailability] = useState(false)

  const debounceRef = useRef(null)
  const initialAppliedRef = useRef(false)
  const indicatorScrollRef = useRef(null)

  const indicatorsWithKeys = useMemo(() => {
    return (indicatorList || []).map(indicator => ({
      ...indicator,
      selectionKey: buildIndicatorSelectionKey(indicator, topicList || []),
    }))
  }, [indicatorList, topicList])

  const indicatorsByKey = useMemo(() => {
    const map = {}
    indicatorsWithKeys.forEach(indicator => {
      map[indicator.selectionKey] = indicator
    })
    return map
  }, [indicatorsWithKeys])

  useEffect(() => {
    if (initialAppliedRef.current) return
    if (!indicatorList || indicatorList.length === 0) return

    const match = indicatorsWithKeys.find(ind => {
      if (initialIndicatorId && String(ind.id) === String(initialIndicatorId)) {
        return !initialTopicId || String(ind.foreign_topic) === String(initialTopicId)
      }
      if (!initialIndicator) return false
      if (ind.name !== initialIndicator) return false
      return !initialTopicId || String(ind.foreign_topic) === String(initialTopicId)
    })

    if (match) {
      initialAppliedRef.current = true
      setSelectedIndicators([match.selectionKey])
      setOpenPanel('countries')
    }
  }, [initialIndicator, initialIndicatorId, initialTopicId, indicatorList, indicatorsWithKeys])

  const indicatorTopicMap = useMemo(() => {
    const map = {}
    if (indicatorsWithKeys && topicList) {
      const topicById = {}
      topicList.forEach(t => {
        topicById[t.id] = {
          raw: t.name,
          label: normalizeTopicName(t.name),
        }
      })
      indicatorsWithKeys.forEach(ind => {
        map[ind.selectionKey] = topicById[ind.foreign_topic] || { raw: '', label: '' }
      })
    }
    return map
  }, [indicatorsWithKeys, topicList])

  const groupedIndicators = useMemo(() => {
    if (!indicatorsWithKeys || !topicList) return {}

    const areaById = {}
    if (areaList) {
      areaList.forEach(a => {
        areaById[a.id] = { code: a.name, label: a.beauty_name || a.name }
      })
    }

    const topicById = {}
    topicList.forEach(t => {
      const rawName = t.name || ''
      const fallbackCode = rawName.includes('_') ? rawName.split('_')[1] : rawName
      topicById[t.id] = {
        code: fallbackCode,
        label: t.beauty_name || fallbackCode,
        areaId: t.foreign_area,
      }
    })

    const groups = {}
    indicatorsWithKeys.forEach(ind => {
      const topic = topicById[ind.foreign_topic] || { code: '', label: '', areaId: null }
      const areaId = topic.areaId
      const area = areaById[areaId] || { code: 'Other', label: 'Other' }
      const areaKey = areaId ?? 'other'
      const topicKey = ind.foreign_topic ?? 'other'

      if (!groups[areaKey]) groups[areaKey] = { meta: area, topics: {} }
      if (!groups[areaKey].topics[topicKey]) {
        groups[areaKey].topics[topicKey] = { meta: topic, indicators: [] }
      }
      groups[areaKey].topics[topicKey].indicators.push(ind)
    })

    const sorted = {}
    Object.entries(groups)
      .sort(([, a], [, b]) => a.meta.label.localeCompare(b.meta.label))
      .forEach(([areaKey, areaData]) => {
        sorted[areaKey] = {
          meta: areaData.meta,
          topics: {},
        }
        Object.entries(areaData.topics)
          .sort(([, a], [, b]) => {
            const areaLabel = areaData.meta.label || areaData.meta.code
            const weightA = getTopicSortWeight(areaLabel, a.meta.label)
            const weightB = getTopicSortWeight(areaLabel, b.meta.label)
            if (weightA !== weightB) return weightA - weightB
            return a.meta.label.localeCompare(b.meta.label)
          })
          .forEach(([topicKey, topicData]) => {
            sorted[areaKey].topics[topicKey] = {
              meta: topicData.meta,
              indicators: topicData.indicators.slice().sort((a, b) => a.name.localeCompare(b.name)),
            }
          })
      })
    return sorted
  }, [indicatorsWithKeys, topicList, areaList, getTopicSortWeight])

  const fetchAvailability = useCallback(async (countries) => {
    if (countries.length === 0) {
      setIndicatorAvailability({})
      return
    }
    setLoadingAvailability(true)
    try {
      const { data } = await axios.post('/api/v1/data/indicators-for-countries/', { countries })
      const map = {}
      if (data.indicators) {
        data.indicators.forEach(item => {
          const selectionKey = buildIndicatorSelectionKey({
            id: item.indicator_id,
            foreign_topic: item.topic_id,
            name: item.indicator,
          })

          map[selectionKey] = {
            available: item.available_countries,
            unavailable: item.unavailable_countries,
            has_any_data: item.has_any_data,
          }
        })
      }
      setIndicatorAvailability(map)
    } catch (error) {
      console.error('Error fetching indicator availability:', error)
    } finally {
      setLoadingAvailability(false)
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchAvailability(selectedCountries)
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [selectedCountries, fetchAvailability])

  const toggleCountry = (name) => {
    setSelectedCountries(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    )
  }

  const selectAllCountries = () => {
    const allNames = (countryList || []).map(c => c.name)
    setSelectedCountries(allNames)
  }

  const clearAllCountries = () => setSelectedCountries([])

  const toggleIndicator = (selectionKey) => {
    setSelectedIndicators(prev =>
      prev.includes(selectionKey) ? prev.filter(i => i !== selectionKey) : [...prev, selectionKey]
    )
  }

  const handleSearch = () => {
    if (selectedIndicators.length === 0 || selectedCountries.length === 0) return
    const selectedIndicatorObjects = selectedIndicators
      .map(key => indicatorsByKey[key])
      .filter(Boolean)
    onSearch(selectedIndicatorObjects, selectedCountries, {})
  }

  const clearAllWizardSelections = () => {
    setSelectedCountries([])
    setSelectedIndicators([])
    setIndicatorAvailability({})
    setCountrySearch('')
    setIndicatorSearch('')
    setOpenPanel('countries')
  }

  const handleReset = () => {
    setSelectedIndicators([])
    setIndicatorSearch('')
    setOpenPanel('indicators')
  }

  const handleResetAll = () => {
    clearAllWizardSelections()
    if (onResetAll) {
      onResetAll()
    }
  }

  const scrollIndicators = (direction) => {
    if (!indicatorScrollRef.current) return
    indicatorScrollRef.current.scrollBy({
      top: direction * 260,
      behavior: 'smooth',
    })
  }

  const filteredCountries = (countryList || []).filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  )

  const filteredIndicators = indicatorsWithKeys.filter(indicator =>
    indicator.name.toLowerCase().includes(indicatorSearch.toLowerCase()) ||
    (indicatorTopicMap[indicator.selectionKey]?.label || '').toLowerCase().includes(indicatorSearch.toLowerCase())
  )

  const noDataPairs = selectedIndicators.flatMap(indicatorKey => {
    const avail = indicatorAvailability[indicatorKey]
    if (!avail || avail.has_any_data === null) return []

    return selectedCountries
      .filter(country => !includesCountry(avail.available, country))
      .map(country => ({ indicatorKey, country }))
  })

  const canVisualize = selectedIndicators.length > 0 && selectedCountries.length > 0

  const countrySummary = selectedCountries.length > 0
    ? `${selectedCountries.length} selected`
    : null
  const indicatorSummary = selectedIndicators.length > 0
    ? `${selectedIndicators.length} selected`
    : null

  const renderAvailBadge = (indicatorKey) => {
    if (selectedCountries.length === 0) return null
    const avail = indicatorAvailability[indicatorKey]
    if (!avail) return null
    const total = selectedCountries.length
    const nAvail = avail.available.length
    if (nAvail === total) {
      return <span className="avail-badge all">{nAvail}/{total}</span>
    } else if (nAvail > 0) {
      return <span className="avail-badge partial">{nAvail}/{total}</span>
    }
    return <span className="avail-badge none">No data</span>
  }

  return (
    <div className="search-wizard">
      <div className="wizard-panel">
        <PanelHeader
          step={1}
          title="Countries"
          summary={countrySummary}
          isOpen={openPanel === 'countries'}
          isDone={selectedCountries.length > 0}
          onToggle={() => setOpenPanel(openPanel === 'countries' ? null : 'countries')}
        />
        {openPanel === 'countries' && (
          <div className="panel-body panel-body-countries">
            <input
              className="wizard-search-input"
              placeholder="Search countries…"
              value={countrySearch}
              onChange={e => setCountrySearch(e.target.value)}
            />
            <div
              className="select-all-row"
              onClick={selectedCountries.length === (countryList || []).length ? clearAllCountries : selectAllCountries}
            >
              <input
                type="checkbox"
                readOnly
                checked={selectedCountries.length === (countryList || []).length && (countryList || []).length > 0}
                style={{ accentColor: 'var(--idb-blue)', width: 14, height: 14 }}
              />
              {selectedCountries.length === (countryList || []).length && (countryList || []).length > 0
                ? 'Deselect All'
                : 'Select All'}
            </div>
            {filteredCountries.map(c => (
              <div
                key={c.name}
                className={`country-item${selectedCountries.includes(c.name) ? ' selected' : ''}`}
                onClick={() => toggleCountry(c.name)}
              >
                <input type="checkbox" readOnly checked={selectedCountries.includes(c.name)} />
                <img
                  className="country-flag"
                  src={getFlagUrl(c.name)}
                  alt={`${c.name} flag`}
                  loading="lazy"
                  width="20"
                  height="15"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none'
                    event.currentTarget.nextSibling.style.display = 'inline-flex'
                  }}
                />
                <span className="country-flag" style={{ display: 'none' }}>{getFlagFallback(c.name)}</span>
                <span className="country-name">{c.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="wizard-panel">
        <PanelHeader
          step={2}
          title="Indicators"
          summary={indicatorSummary}
          isOpen={openPanel === 'indicators'}
          isDone={selectedIndicators.length > 0}
          onToggle={() => setOpenPanel(openPanel === 'indicators' ? null : 'indicators')}
        />
        {openPanel === 'indicators' && (
          <div className="panel-body panel-body-indicators">
            <input
              className="wizard-search-input wizard-search-input-sticky"
              placeholder="Search indicators or topics…"
              value={indicatorSearch}
              onChange={e => setIndicatorSearch(e.target.value)}
            />
            {loadingAvailability && (
              <div className="availability-loading">Loading availability…</div>
            )}

            <div className="indicator-scroll-frame">
              <button
                type="button"
                className="indicator-scroll-btn indicator-scroll-btn-up"
                onClick={() => scrollIndicators(-1)}
                aria-label="Scroll indicators up"
              >
                <i className="fa-solid fa-chevron-up"></i>
              </button>

              <div className="indicator-scroll-region" ref={indicatorScrollRef}>
                {indicatorSearch.trim() ? (
                  filteredIndicators.length === 0
                    ? <div className="availability-loading">No indicators match</div>
                    : filteredIndicators.map(indicator => (
                      <div
                        key={indicator.selectionKey}
                        className={`indicator-item${selectedIndicators.includes(indicator.selectionKey) ? ' selected' : ''}`}
                        onClick={() => toggleIndicator(indicator.selectionKey)}
                      >
                        <input type="checkbox" readOnly checked={selectedIndicators.includes(indicator.selectionKey)} />
                        <div className="indicator-item-info">
                          <div className="indicator-item-name">{indicator.name}</div>
                          {indicatorTopicMap[indicator.selectionKey]?.label && (
                            <div className="indicator-item-topic">{indicatorTopicMap[indicator.selectionKey].label}</div>
                          )}
                        </div>
                        {renderAvailBadge(indicator.selectionKey)}
                      </div>
                    ))
                ) : (
                  Object.entries(groupedIndicators).map(([areaKey, areaData]) => (
                    <div key={areaKey} className="ind-area-group">
                      <div className="ind-area-header">
                        <span className="ind-area-label">{areaData.meta.label}</span>
                      </div>
                      {Object.entries(areaData.topics).map(([topicKey, topicData]) => (
                        <div key={topicKey} className="ind-topic-group">
                          <div className="ind-topic-header">{topicData.meta.label}</div>
                          {topicData.indicators.map(indicator => (
                            <div
                              key={indicator.selectionKey}
                              className={`indicator-item${selectedIndicators.includes(indicator.selectionKey) ? ' selected' : ''}`}
                              onClick={() => toggleIndicator(indicator.selectionKey)}
                            >
                              <input type="checkbox" readOnly checked={selectedIndicators.includes(indicator.selectionKey)} />
                              <div className="indicator-item-info">
                                <div className="indicator-item-name">{indicator.name}</div>
                              </div>
                              {renderAvailBadge(indicator.selectionKey)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>

              <button
                type="button"
                className="indicator-scroll-btn indicator-scroll-btn-down"
                onClick={() => scrollIndicators(1)}
                aria-label="Scroll indicators down"
              >
                <i className="fa-solid fa-chevron-down"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="wizard-visualize-footer">
        <div className="wizard-summary-bar">
          <div className="wizard-summary-stat-pill">
            <span className="wizard-summary-num">{selectedCountries.length}</span>
            <span className="wizard-summary-lbl">Countries</span>
          </div>
          <div className="wizard-summary-stat-pill">
            <span className="wizard-summary-num">{selectedIndicators.length}</span>
            <span className="wizard-summary-lbl">Indicators</span>
          </div>
        </div>

        {noDataPairs.length > 0 && (
          <div className="wizard-warning-box">
            <strong>⚠ No data for:</strong>
            <ul>
              {noDataPairs.map(({ indicatorKey, country }) => {
                const key = `${indicatorKey}|||${country}`
                return <li key={key}>{indicatorsByKey[indicatorKey]?.name || indicatorKey} – {country}</li>
              })}
            </ul>
          </div>
        )}

        <button
          className="btn-visualize"
          disabled={!canVisualize || loading}
          onClick={handleSearch}
        >
          {loading ? '⏳ Loading…' : '📊 Update Visualization'}
        </button>
        <button className="btn-reset-wizard" onClick={handleReset}>
          Reset Indicators
        </button>
        <button className="btn-reset-wizard" onClick={handleResetAll}>
          Reset All
        </button>
      </div>
    </div>
  )
}

export default SearchWizard
